<template>
  <v-main
    style="max-width: 1024px; margin: auto"
    class="mb-16"
    :class="[
      leftSideBar ? 'myrep moveUp' : '',
      $vuetify.display.xs ? 'minHeightMobile' : 'minHeightDesktop',
    ]"
  >
    <!-- main layout -->
    <v-container>
      <v-row class="text-white mt-2">
        <!-- Account Settings title -->
        <v-col cols="12">
          <v-row align-content="center" justify="start">
            <v-col cols="auto" class="center">
              <v-img
                class="account-img"
                src="@/assets/acct-settings-icon.svg"
                alt=""
                role="presentation"
                width="36px"
                height="36px"
              />
            </v-col>
            <v-col>
              <h1 role="heading" aria-level="1" class="text-h1">
                {{ $t('L_account_settings') }}
              </h1>
            </v-col>
          </v-row>
        </v-col>
        <!-- Updates made in Account Settings apply... -->
        <v-col cols="12">
          <p class="body-1">{{ $t('L_account_setting_sub_text') }}</p>
        </v-col>
      </v-row>
      <v-row>
        <!-- Update Password -->
        <v-col cols="12" sm="6" class="pt-1 pb-1 pl-1 pr-1">
          <UpdatePassword />
        </v-col>
        <!-- Update User ID -->
        <v-col cols="12" sm="6" class="pt-1 pb-1 pl-1 pr-1">
          <UpdateUserID />
        </v-col>
        <!-- Update Biometrics Preference -->
        <v-col
          cols="12"
          sm="6"
          v-if="mobileMeta.canUseBiometrics && mobileMeta.hasStrongBiometricsStrengthLevel"
          class="pt-1 pb-1 pl-1 pr-1"
        >
          <UpdateBiometricsPreference />
        </v-col>
        <!-- Update Contact Information -->
        <v-col cols="12" sm="6" class="pt-1 pb-1 pl-1 pr-1">
          <UpdateContact
            @verify-go-to-page-one="verifyPhoneModalGoToPageOne"
            @verify-bypass-page-one="verifyPhoneModalBypassPageOne"
            ref="updateContactRef"
          />
        </v-col>
        <!-- ! Removing from UI temporary due to OKTA
        <v-col cols="12" sm="6">
          <UpdateDevice />
        </v-col> -->
        <v-col cols="12" sm="6" v-if="showUpdateCommunicationCard" class="pt-1 pb-1 pl-1 pr-1">
          <UpdateCommunication />
        </v-col>
        <v-col
          cols="12"
          sm="6"
          v-if="updateInvestmentGoPaperlessEnabled && investmentCommEnabled"
          class="pt-1 pb-1 pl-1 pr-1"
        >
          <UpdateInvestmentCommunication :invdata="investmentCommData" />
        </v-col>
      </v-row>
      <v-row class="pt-1 mt-0 mb-4">
        <v-col cols="12" class="pt-0 mt-0">
          <VerifyPhoneWrapper
            :allow-edit="false"
            :show="showVerifyPhoneWrapper"
            @show-successful-verify="handleCallsGetAlerts"
            @close-verify-phone-dialog="closeVerifyPhoneDialog"
            @gotoupdatecontact2="goToUpdateContact"
            ref="verifyacc"
          />
          <SuccessVerifyPhone
            v-if="showSuccessfulVerify"
            @close-success-verify-phone="closeSuccessVerifyPhone"
          />
        </v-col>
        <!-- Mobile Meta Information -->
        <v-col cols="12" class="pt-0 mt-0 pl-1 pr-1 mb-4">
          <v-card height="100%" class="pa-4 pt-1 mt-0 mb-4">
            <v-card-title>
              <v-icon
                color="primary"
                @click="toggleMinimizeVersionInfo"
                v-if="minimizeVersionInfo"
                :icon="mdiChevronDown"
              />
              <v-icon
                color="primary"
                @click="toggleMinimizeVersionInfo"
                v-else-if="!minimizeVersionInfo"
                :icon="mdiChevronUp"
              />
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-if="!minimizeVersionInfo">
              <p>
                {{ $t('L_version') }} <strong>{{ getVersion }}</strong>
              </p>
              <p>
                unlockModePreference: <strong>{{ mobileMeta.unlockModePreference }}</strong>
              </p>
              <p>
                canUseBiometrics: <strong>{{ mobileMeta.canUseBiometrics }}</strong>
              </p>
              <p>
                hasStrongBiometricsStrengthLevel:
                <strong>{{ mobileMeta.hasStrongBiometricsStrengthLevel }}</strong>
              </p>
              <br />
              <p>Vault Config:</p>
              <ul class="px-8">
                <li v-for="(v, k) in mobileMeta.vaultConfig" :key="k">
                  {{ k }}: <strong>{{ v }}</strong>
                </li>
              </ul>
              <br />
              <p>Device Info:</p>
              <ul class="px-8">
                <li v-for="(v, k) in mobileMeta.deviceInfo" :key="k">
                  {{ k }}: <strong>{{ v }}</strong>
                </li>
              </ul>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import {
  getUnlockModePreference,
  canUseBiometrics,
  hasStrongBiometricsStrengthLevel,
  getVaultConfig,
} from '@/plugins/auth/utils/sessionManager';
import appPackage from '../../../../package.json';
import { FEATURE_FLAGS } from '@/shared/models/constants.models';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapActions, mapState, mapStores } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import VerifyPhoneWrapper from '@/components/VerifyPhoneWrapper/VerifyPhoneWrapper.vue';
import SuccessVerifyPhone from './components/MyAccountSuccessVerifyPhone.vue';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import UpdatePassword from './components/UpdatePassword.vue';
import UpdateUserID from './components/UpdateUserID.vue';
import UpdateBiometricsPreference from './components/UpdateBiometricsPreference.vue';
import UpdateContact from './components/UpdateContact.vue';
import UpdateCommunication from './components/UpdateCommunication.vue';
import UpdateInvestmentCommunication from './components/UpdateInvestmentCommunication.vue';
import type { IMobileMeta } from '@/modules/home/store/homeStore.model.ts';
// import UpdateDevice from './components/UpdateDevice.vue'; // ! Removing from UI temporary due to OKTA
import bff from '@/shared/api/bff';

const componentOptions: ComponentOptions = {
  name: 'MyAccount',
  components: {
    VerifyPhoneWrapper,
    UpdatePassword,
    UpdateUserID,
    UpdateBiometricsPreference,
    UpdateContact,
    UpdateCommunication,
    UpdateInvestmentCommunication,
    // UpdateDevice, // Removing from UI temporary due to OKTA
    SuccessVerifyPhone,
  },
  data: () => ({
    minimizeVersionInfo: true,
    showSuccessfulVerify: false,
    showVerifyPhoneWrapper: false,
    mobileMeta: {
      unlockModePreference: null,
      canUseBiometrics: false,
      hasStrongBiometricsStrengthLevel: false,
      vaultConfig: null,
      deviceInfo: null,
    } as IMobileMeta,
    appPackage,
    mdiChevronDown,
    mdiChevronUp,
    investmentCommEnabled: false,
    investmentCommData: [],
  }),
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, ['containsFeature', 'features', 'verifyPhonePageToLand']),
    ...mapState(usePolicyStore, ['policies']),
    showUpdateCommunicationCard() {
      return this.updateCommEnabled && this.validPolicies.length > 0;
    },
    updateCommEnabled() {
      return this.containsFeature(FEATURE_FLAGS.ECOMM);
    },
    updateInvestmentGoPaperlessEnabled() {
      return this.containsFeature(FEATURE_FLAGS.ACCOUNT_SETTING_INVESTMENT_PAPERLESS);
    },
    getVersion() {
      return appPackage.version;
    },
    validPolicies() {
      return this.policies.filter(
        (policy: { contractId: string | number; access: string }) =>
          +policy.contractId !== 0 && policy.access !== 'FULL'
      );
    },
  },
  methods: {
    ...mapActions(useGlobalStore, ['getAlerts', 'dismissAlert', 'completeAlert']),
    ...mapActions(usePolicyStore, {
      getPolicyOverview: 'getPolicyOverview',
    }),
    toggleMinimizeVersionInfo() {
      this.minimizeVersionInfo = !this.minimizeVersionInfo;
    },
    handleCallsGetAlerts() {
      this.showVerifyPhoneWrapper = false;
      this.getAlerts();
      this.showSuccessfulVerify = true;
    },
    closeVerifyPhoneDialog() {
      this.showVerifyPhoneWrapper = false;
      this.getAlerts();
      this.showSuccessfulVerify = false;
    },
    goToUpdateContact() {
      this.showVerifyPhoneWrapper = false;
      this.$refs.updateContactRef.showUpdatePhoneDialog = true;
    },
    closeSuccessVerifyPhone() {
      this.showSuccessfulVerify = false;
    },
    verifyPhoneModalGoToPageOne() {
      this.globalStore.verifyPhonePageToLand = 'first';
      this.showVerifyPhoneWrapper = true;
    },
    verifyPhoneModalBypassPageOne(phoneNum: string) {
      this.globalStore.verifyPhonePageToLand = '';
      this.$refs.verifyacc.bypassPageOne(phoneNum);
      this.showVerifyPhoneWrapper = true;
    },
  },
  async mounted() {
    this.getPolicyOverview();
    const preferences = await bff.getInvestmentsCommunicationsPreferences();
    // preferences might be undefined if user has no investment accounts
    if (preferences?.channel) {
      this.investmentCommEnabled = true;
      this.investmentCommData = preferences;
    }
    // TODO: handle this onUpdated or even on vault config changes
    await Promise.all([
      getUnlockModePreference(),
      canUseBiometrics(),
      hasStrongBiometricsStrengthLevel(),
      getVaultConfig(),
      CapacitorFacade.getDeviceInfo(),
    ]).then(
      ([
        _unlockModePreference,
        _canUseBiometrics,
        _hasStrongBiometricsStrengthLevel,
        _vaultConfig,
        _deviceInfo,
      ]) => {
        this.mobileMeta.unlockModePreference = _unlockModePreference;
        this.mobileMeta.canUseBiometrics = _canUseBiometrics;
        this.mobileMeta.hasStrongBiometricsStrengthLevel = _hasStrongBiometricsStrengthLevel;
        this.mobileMeta.vaultConfig = _vaultConfig;
        this.mobileMeta.deviceInfo = _deviceInfo;
      }
    );
  },
};

export default defineComponent(componentOptions);
</script>
<style lang="scss" scoped>
.minHeightMobile {
  min-height: 1050px;
}
.minHeightDesktop {
  min-height: 950px;
}
.center {
  display: flex;
  justify-content: center;
}

#main-flex {
  margin-top: 40px;

  h3 {
    color: black;
  }

  img {
    display: block;
  }

  span {
    color: #757576;
  }
}

@media (min-width: 600px) {
  #main-flex {
    :deep(.layout) {
      .pa8 {
        &:nth-child(2n-1) {
          padding-right: 8px;
        }
        &:nth-child(2n) {
          padding-left: 8px;
        }
      }
    }

    :deep(.pa8) {
      padding: 8px 0;
    }

    :deep(.pt8) {
      padding-top: 8px;
    }

    :deep(.pl8) {
      padding-left: 8px;
    }

    :deep(.mt24) {
      margin-top: 24px;
    }

    :deep(.mt-48) {
      margin-top: 48px;
    }
  }
}

@media (max-width: 599px) {
  #main-flex {
    margin-top: 24px;

    :deep(.mt8) {
      margin-top: 8px;
    }
  }
}

@media (max-width: 959px) {
  :deep(.lh32) {
    line-height: 32px;
  }
}
</style>
